import { userService } from '@/api/user/userApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { handleGlobalError } from '@/utils/error-handler';
import { FileText, ExternalLink, LogOut } from 'lucide-react';
import React, { useState } from 'react';

interface TermsAcceptanceScreenProps {
  onTermsAccepted: () => void;
}

export const TermsAcceptanceScreen: React.FC<TermsAcceptanceScreenProps> = ({
  onTermsAccepted,
}) => {
  const { clearTokens } = useAuth();
  const [hasAccepted, setHasAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await userService.acceptTerms();
      toast({
        title: 'Términos aceptados',
        description: 'Has aceptado los términos y condiciones correctamente.',
      });
      onTermsAccepted();
    } catch (error) {
      handleGlobalError(
        error,
        'Error al aceptar términos',
        'No se pudieron aceptar los términos. Por favor, intenta nuevamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearTokens();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <FileText size={32} className="text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Términos y Condiciones
          </CardTitle>
          <p className="mt-2 text-gray-600">
            Para continuar usando la aplicación, necesitas aceptar nuestros
            términos y condiciones.
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start gap-3">
              <FileText
                size={20}
                className="mt-0.5 flex-shrink-0 text-blue-600"
              />
              <div>
                <h4 className="mb-1 font-semibold text-blue-900">
                  Revisa nuestros términos
                </h4>
                <p className="text-sm text-blue-800 mb-3">
                  Por favor, lee atentamente los términos y condiciones antes
                  de aceptar.
                </p>
                <a
                  href="https://getmatchapp.com/terminos-condiciones"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Ver términos y condiciones
                  <ExternalLink size={16} className="ml-1" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
            <Checkbox
              id="terms"
              checked={hasAccepted}
              onCheckedChange={(checked) => setHasAccepted(checked === true)}
              className="mt-1"
            />
            <Label
              htmlFor="terms"
              className="text-sm text-gray-700 cursor-pointer leading-relaxed"
            >
              He leído y acepto los{' '}
              <a
                href="https://getmatchapp.com/terminos-condiciones"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                términos y condiciones
              </a>{' '}
              de GetMatch.
            </Label>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleLogout}
              className="flex-1"
            >
              <LogOut size={18} className="mr-2" />
              Cerrar Sesión
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1"
              disabled={!hasAccepted || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Aceptando...
                </>
              ) : (
                <>
                  <FileText size={18} className="mr-2" />
                  Aceptar Términos
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
